// src/web/stores/filters.ts
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'

// Browse mode: 'home' = folder grid, 'all' = all files, 'folder' = specific folder
type BrowseMode = 'home' | 'all' | 'folder'

interface FilterState {
	// Search
	searchQuery: string
	debouncedSearchQuery: string

	// Browse mode
	browseMode: BrowseMode

	// Filters
	selectedFolder: string | null
	fieldFilters: Record<string, string[]>
	lifecycleState: string | null
	fileType: string | null

	// Sorting
	sortBy: string
	sortOrder: 'asc' | 'desc'

	// Pagination
	page: number
	pageSize: number

	// View options
	viewMode: 'grid' | 'list'

	// Actions
	setSearchQuery: (query: string) => void
	setDebouncedSearchQuery: (query: string) => void
	setBrowseMode: (mode: BrowseMode) => void
	setSelectedFolder: (folder: string | null) => void
	setFieldFilter: (fieldId: string, values: string[]) => void
	removeFieldFilter: (fieldId: string) => void
	setLifecycleState: (state: string | null) => void
	setFileType: (type: string | null) => void
	setSortBy: (sortBy: string) => void
	setSortOrder: (order: 'asc' | 'desc') => void
	setPage: (page: number) => void
	setPageSize: (size: number) => void
	setViewMode: (mode: 'grid' | 'list') => void
	clearFilters: () => void
	resetPagination: () => void
	goToBrowseHome: () => void
}

const initialState = {
	searchQuery: '',
	debouncedSearchQuery: '',
	browseMode: 'home' as BrowseMode,
	selectedFolder: null,
	fieldFilters: {},
	lifecycleState: null,
	fileType: null,
	sortBy: 'dateStaged',
	sortOrder: 'desc' as const,
	page: 1,
	pageSize: 18,
	viewMode: 'grid' as const,
}

export const useFilterStore = create<FilterState>((set) => ({
	...initialState,

	setSearchQuery: (searchQuery) => set({ searchQuery }),

	setDebouncedSearchQuery: (debouncedSearchQuery) =>
		set({
			debouncedSearchQuery,
			page: 1, // Reset to first page on search
		}),

	setBrowseMode: (browseMode) => set({ browseMode }),

	setSelectedFolder: (selectedFolder) =>
		set({
			selectedFolder,
			browseMode: !selectedFolder ? 'home' : selectedFolder === 'all' ? 'all' : 'folder',
			page: 1,
		}),

	setFieldFilter: (fieldId, values) =>
		set((state) => ({
			fieldFilters: {
				...state.fieldFilters,
				[fieldId]: values,
			},
			page: 1,
		})),

	removeFieldFilter: (fieldId) =>
		set((state) => {
			const { [fieldId]: _, ...rest } = state.fieldFilters
			return { fieldFilters: rest, page: 1 }
		}),

	setLifecycleState: (lifecycleState) =>
		set({
			lifecycleState,
			page: 1,
		}),

	setFileType: (fileType) =>
		set({
			fileType,
			page: 1,
		}),

	setSortBy: (sortBy) => set({ sortBy }),

	setSortOrder: (sortOrder) => set({ sortOrder }),

	setPage: (page) => set({ page }),

	setPageSize: (pageSize) =>
		set({
			pageSize,
			page: 1,
		}),

	setViewMode: (viewMode) => set({ viewMode }),

	clearFilters: () =>
		set({
			searchQuery: '',
			debouncedSearchQuery: '',
			selectedFolder: null,
			fieldFilters: {},
			lifecycleState: null,
			fileType: null,
			page: 1,
		}),

	resetPagination: () => set({ page: 1 }),

	goToBrowseHome: () =>
		set({
			browseMode: 'home',
			selectedFolder: null,
			page: 1,
		}),
}))

// Selector for building query params - uses shallow comparison to prevent unnecessary re-renders
export const useFilterParams = () => {
	return useFilterStore(
		useShallow((state) => ({
			query: state.debouncedSearchQuery || undefined,
			folder:
				state.selectedFolder && state.selectedFolder !== 'all' ? state.selectedFolder : undefined,
			fieldFilters:
				Object.keys(state.fieldFilters).length > 0 ? JSON.stringify(state.fieldFilters) : undefined,
			lifecycleState: state.lifecycleState || undefined,
			fileType: state.fileType || undefined,
			page: state.page,
			pageSize: state.pageSize,
			sortBy: state.sortBy,
			sortOrder: state.sortOrder,
		}))
	)
}

// Filter order store (persisted to localStorage)
interface FilterOrderState {
	// Custom order of filter field IDs (null means use default order)
	fieldOrder: string[] | null
	// Hidden field IDs
	hiddenFields: string[]
	// Edit mode state
	isEditMode: boolean
	// Actions
	setFieldOrder: (order: string[]) => void
	resetFieldOrder: () => void
	toggleFieldVisibility: (fieldId: string) => void
	setEditMode: (enabled: boolean) => void
	toggleEditMode: () => void
}

export const useFilterOrderStore = create<FilterOrderState>()(
	persist(
		(set) => ({
			fieldOrder: null,
			hiddenFields: [],
			isEditMode: false,

			setFieldOrder: (fieldOrder) => set({ fieldOrder }),

			resetFieldOrder: () => set({ fieldOrder: null, hiddenFields: [] }),

			toggleFieldVisibility: (fieldId) =>
				set((state) => ({
					hiddenFields: state.hiddenFields.includes(fieldId)
						? state.hiddenFields.filter((id) => id !== fieldId)
						: [...state.hiddenFields, fieldId],
				})),

			setEditMode: (isEditMode) => set({ isEditMode }),

			toggleEditMode: () => set((state) => ({ isEditMode: !state.isEditMode })),
		}),
		{
			name: 'filter-order-storage',
			partialize: (state) => ({ fieldOrder: state.fieldOrder, hiddenFields: state.hiddenFields }),
		}
	)
)
